/**
 * schoolRosterService – the browser side of /school-roster.
 *
 * School admins, principals, partners and admins change a school's teachers and
 * classes through the server, which checks the school and writes a class and
 * its members' user documents in one transaction. firestore.rules no longer
 * lets those roles write class membership directly.
 *
 * Every call resolves to `{ ok, data?, message? }` rather than throwing, so a
 * caller can show the server's reason ("That class is full") as it is.
 */

import api from '../config/axios';

export interface RosterResult<T> {
  ok: boolean;
  data?: T;
  /** The server's explanation when ok is false. */
  message?: string;
  code?: string;
}

export type StudentOutcome =
  | 'added'
  | 'already_in_class'
  | 'class_full'
  | 'removed'
  | 'not_in_class'
  | 'not_found'
  | 'other_school'
  | 'not_approved';

export interface StudentChangeResult {
  studentIds: string[];
  report: Array<{ studentId: string; outcome: StudentOutcome }>;
  moved: { classId: string; report: Array<{ studentId: string; outcome: StudentOutcome }> } | null;
}

async function call<T>(method: 'post' | 'patch' | 'delete', path: string, body: Record<string, unknown>): Promise<RosterResult<T>> {
  try {
    const res =
      method === 'delete'
        ? await api.delete(path, { data: body })
        : await api[method](path, body);
    if (res.status >= 400 || !res.data?.success) {
      return { ok: false, message: res.data?.message || `Request failed (HTTP ${res.status})`, code: res.data?.error };
    }
    return { ok: true, data: res.data.data as T };
  } catch (err: any) {
    console.error(`[schoolRoster] ${method.toUpperCase()} ${path} failed:`, err);
    return { ok: false, message: err?.response?.data?.message || 'The server could not be reached.' };
  }
}

/** schoolId is only read for admins and partners; staff of a school are held to their own. */
export const decideTeacher = (
  teacherId: string,
  decision: 'approve' | 'reject' | 'revoke',
  opts: { schoolId?: string | null; classIds?: string[]; classTeacherOf?: string[]; reason?: string } = {}
) =>
  call<{ status: string; classIds: string[] }>('post', `/school-roster/teachers/${encodeURIComponent(teacherId)}/decision`, {
    decision,
    ...opts,
  });

export const createClass = (input: {
  schoolId?: string | null;
  class_name: string;
  subject?: string | null;
  section?: string | null;
  academic_year?: string | null;
  class_teacher_id?: string | null;
  curriculum?: string | null;
}) => call<{ id: string }>('post', '/school-roster/classes', input);

export const updateClass = (
  classId: string,
  input: { schoolId?: string | null; class_name?: string; subject?: string | null; section?: string | null; academic_year?: string | null }
) => call<{ id: string }>('patch', `/school-roster/classes/${encodeURIComponent(classId)}`, input);

export const archiveClass = (classId: string, schoolId?: string | null) =>
  call<{ id: string }>('delete', `/school-roster/classes/${encodeURIComponent(classId)}`, { schoolId });

export const changeClassTeachers = (
  classId: string,
  input: { schoolId?: string | null; add?: string[]; remove?: string[]; classTeacherId?: string | null }
) =>
  call<{ teacherIds: string[]; classTeacherId: string | null; added: string[]; removed: string[] }>(
    'post',
    `/school-roster/classes/${encodeURIComponent(classId)}/teachers`,
    input
  );

export const changeClassStudents = (
  classId: string,
  input: { schoolId?: string | null; add?: string[]; remove?: string[]; moveTo?: string | null }
) => call<StudentChangeResult>('post', `/school-roster/classes/${encodeURIComponent(classId)}/students`, input);

export const matchStudents = (identifiers: string[], schoolId?: string | null) =>
  call<{ matched: Array<{ input: string; studentId: string; name: string; email: string }>; unmatched: string[] }>(
    'post',
    '/school-roster/students/match',
    { identifiers, schoolId }
  );

/**
 * Approve a student (placing them in the class they asked for, or the class
 * given) or reject them. A teacher may decide only for a class they teach.
 * Staff may approve without placing (placeNowhere).
 */
export const decideStudent = (
  studentId: string,
  decision: 'approve' | 'reject',
  opts: { schoolId?: string | null; classId?: string | null; placeNowhere?: boolean; reason?: string } = {}
) =>
  call<{ status: string; classId: string | null }>('post', `/school-roster/students/${encodeURIComponent(studentId)}/decision`, {
    decision,
    ...opts,
  });

/** Share a class with another teacher of the school, or stop sharing it. */
export const shareClass = (classId: string, teacherId: string, share: boolean, schoolId?: string | null) =>
  call<{ classId: string; teacherId: string; shared: boolean }>('post', `/school-roster/classes/${encodeURIComponent(classId)}/share`, {
    teacherId,
    share,
    schoolId,
  });

/** Plain-language summary of an enrollment report, for a toast. */
export function describeStudentChange(report: StudentChangeResult['report']): string {
  const count = (o: StudentOutcome) => report.filter((r) => r.outcome === o).length;
  const parts = [
    count('added') && `${count('added')} added`,
    count('removed') && `${count('removed')} removed`,
    count('already_in_class') && `${count('already_in_class')} already in the class`,
    count('class_full') && `${count('class_full')} not added: class is full`,
    count('other_school') && `${count('other_school')} from another school`,
    count('not_found') && `${count('not_found')} not found`,
    count('not_approved') && `${count('not_approved')} not yet approved`,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : 'No change';
}
